import type { GlobalEvent, PendingVisit } from '@inertiajs/core'
import type { Component } from 'svelte'
import { createPageLoader } from './utils/pageLoader'

type PageModule = { default: Component }
export type PreloadablePage = 'books/show' | 'profiles/show'

const imports = import.meta.glob<PageModule>(
  ['../views/**/*.svelte', '!../views/components/**/*.svelte'],
  { eager: false }
)
const pages = createPageLoader(
  Object.fromEntries(
    Object.entries(imports).map(([path, importer]) => [
      path.slice('../views/'.length, -'.svelte'.length),
      importer,
    ])
  )
)

let navigation: PendingVisit | undefined

export const preloadPage = (name: PreloadablePage): Promise<void> =>
  pages.preload(name)

export function beforePageNavigation(event: GlobalEvent<'before'>) {
  const { visit } = event.detail
  if (visit.method !== 'get' || visit.async || visit.prefetch) return
  navigation = visit
  if (pages.preloadFailed()) {
    window.location.assign(visit.url.href)
    return false
  }
}

export function finishPageNavigation(event: GlobalEvent<'finish'>) {
  const { visit } = event.detail
  if (
    !visit.async &&
    !visit.prefetch &&
    // Inertia keeps the URL object when copying visit options into a request.
    // An older interrupted request may finish after a new visit to the same URL.
    navigation?.url === visit.url
  ) {
    navigation = undefined
  }
}

export async function resolvePage(name: string): Promise<PageModule> {
  try {
    return await pages.resolve(name)
  } catch (error) {
    // A click can race an unfinished preload. Vite retains failed CSS URLs,
    // so retrying the importer could render the destination without its CSS.
    // Recover only after a real GET navigation, using its intended URL.
    if (pages.preloadFailed() && navigation) {
      window.location.assign(navigation.url.href)
      return new Promise(() => undefined)
    }
    throw error
  }
}
